'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from './SoundExperience.module.css';
import { buildWhatsAppUrl } from '@/utils/whatsapp';
import {
  applyTreatment,
  createSoundEngine,
  stopSoundEngine,
  type SoundSourceType,
} from './soundEngine';

type ScenarioId = 'bondex' | 'bariyerli' | 'cift';

type Scenario = {
  id: ScenarioId;
  tabLabel: string;
  name: string;
  scenario: string;
  desc: string;
  specs: string[];
  whatsappName: string;
  src: SoundSourceType;
  bare: [number, number, number];
  treatMul: [number, number, number];
  dbBare: number;
  dbTreated: number;
  treatLabel: string;
};

const SCENARIOS: Scenario[] = [
  {
    id: 'bondex',
    tabLabel: 'Bondex Sünger',
    name: 'Bondex Sünger',
    scenario: 'Komşu daire konuşma ve gürültüsü',
    desc: 'Rebonded süngerpan yapısı darbe ve titreşim seslerini sönümler. Zemin ve duvar uygulamalarında temel konfor sağlar.',
    specs: ['≈ 18 dB', '50 mm', 'Zemin ve duvar'],
    whatsappName: 'Bondex Sünger',
    src: 'crowd-light',
    bare: [0.47, 0.62, 0.45],
    treatMul: [0.72, 0.55, 0.42],
    dbBare: 74,
    dbTreated: 64,
    treatLabel: 'Sünger uygulanmış',
  },
  {
    id: 'bariyerli',
    tabLabel: 'Bariyerli Bondex Sünger',
    name: 'Bariyerli Bondex Sünger',
    scenario: 'Komşu konuşma ve TV sesi',
    desc: 'Bariyer katmanı hava kaynaklı ses geçişini azaltır. Stüdyo, ofis ve konut projelerinde belirgin fark yaratır.',
    specs: ['≈ 35 ila 40 dB', '33 mm', 'Duvar ve kapı'],
    whatsappName: 'Bariyerli Bondex Sünger',
    src: 'crowd-medium',
    bare: [0.47, 0.92, 0.75],
    treatMul: [0.48, 0.28, 0.32],
    dbBare: 76,
    dbTreated: 52,
    treatLabel: 'Sünger uygulanmış',
  },
  {
    id: 'cift',
    tabLabel: 'Çift Bariyerli Bondex',
    name: 'Çift Bariyerli Bondex Sünger',
    scenario: 'Kalabalık ofis ve koridor gürültüsü',
    desc: 'Çift bariyer katmanı maksimum izolasyon sunar. Kritik projelerde komşu ve dış ortam sesini güçlü şekilde keser.',
    specs: ['≈ 60 ila 65 dB', '36 mm', 'Yüksek izolasyon'],
    whatsappName: 'Çift Bariyerli Bondex Sünger',
    src: 'crowd-heavy',
    bare: [0.55, 0.88, 0.82],
    treatMul: [0.42, 0.22, 0.28],
    dbBare: 78,
    dbTreated: 46,
    treatLabel: 'Sünger uygulanmış',
  },
];

function barPercent(base: number, mul: number, treated: boolean) {
  const value = treated ? base * mul : base;
  return `${Math.round(value * 100)}%`;
}

function barColor(index: number, treated: boolean) {
  if (treated) {
    return index === 0 ? '#34d399' : index === 1 ? '#3dd68c' : 'rgba(61, 214, 140, 0.45)';
  }
  return index === 0 ? '#e8701a' : index === 1 ? '#fc0' : 'rgba(232, 163, 23, 0.45)';
}

export function SoundExperience() {
  const [activeId, setActiveId] = useState<ScenarioId>('bondex');
  const [treated, setTreated] = useState(false);
  const [playing, setPlaying] = useState(false);
  const engineRef = useRef<ReturnType<typeof createSoundEngine>>(null);

  const scenario = useMemo(
    () => SCENARIOS.find((item) => item.id === activeId) ?? SCENARIOS[0],
    [activeId],
  );

  const dbLevel = treated ? scenario.dbTreated : scenario.dbBare;
  const dbReduction = scenario.dbBare - scenario.dbTreated;

  const dbCaption = treated
    ? `${scenario.treatLabel}, ${dbReduction} dB azalma`
    : 'Uygulama öncesi ortam seviyesi';

  const stopAudio = useCallback(() => {
    stopSoundEngine(engineRef.current);
    engineRef.current = null;
    setPlaying(false);
  }, []);

  const startAudio = useCallback(async () => {
    stopAudio();

    const engine = createSoundEngine({
      src: scenario.src,
      dbBare: scenario.dbBare,
      dbTreated: scenario.dbTreated,
    });

    if (!engine) return;

    engineRef.current = engine;
    applyTreatment(engine, treated, {
      src: scenario.src,
      dbBare: scenario.dbBare,
      dbTreated: scenario.dbTreated,
    });

    if (engine.ctx.state === 'suspended') {
      await engine.ctx.resume();
    }

    setPlaying(true);
  }, [scenario, stopAudio, treated]);

  const togglePlay = useCallback(async () => {
    if (playing && engineRef.current?.ctx.state === 'running') {
      await engineRef.current.ctx.suspend();
      setPlaying(false);
      return;
    }

    await startAudio();
  }, [playing, startAudio]);

  useEffect(() => {
    if (engineRef.current) {
      applyTreatment(engineRef.current, treated, {
        src: scenario.src,
        dbBare: scenario.dbBare,
        dbTreated: scenario.dbTreated,
      });
    }
  }, [scenario.dbBare, scenario.dbTreated, scenario.src, treated]);

  useEffect(() => () => stopAudio(), [stopAudio]);

  useEffect(() => {
    if (playing) {
      void startAudio();
    }
  }, [activeId]); // eslint-disable-line react-hooks/exhaustive-deps

  const whatsappUrl = buildWhatsAppUrl(
    `Merhaba, ${scenario.whatsappName} hakkında teklif almak istiyorum.`,
  );

  const bandKeys = ['Bas', 'Orta', 'Tiz'] as const;

  return (
    <section className={styles.section} id="urun-ses-deneyimi" aria-labelledby="sound-exp-title">
      <div className={styles.inner}>
        <header className={styles.head}>
          <div>
            <p className={styles.kicker}>
              <span className={styles.kickerDot} aria-hidden="true" />
              Ürün ses deneyimi
            </p>
            <h2 id="sound-exp-title" className={styles.title}>
              Her ürünü kendi senaryosunda dinleyin
            </h2>
          </div>
          <p className={styles.subtitle}>
            Bir ürün seçin, gerçek hayattaki gürültü senaryosunu dinleyin. Ardından süngeri uygulayıp frekans
            ve seviye farkını aynı anda görün.
          </p>
        </header>

        <div className={styles.rail} role="tablist" aria-label="Ürün seçimi">
          {SCENARIOS.map((item) => (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={item.id === activeId}
              className={`${styles.railBtn} ${item.id === activeId ? styles.railBtnActive : ''}`}
              onClick={() => setActiveId(item.id)}
            >
              {item.tabLabel}
            </button>
          ))}
        </div>

        <div className={`${styles.stage} ${playing ? styles.stagePlaying : ''}`}>
          <div
            className={`${styles.visual} ${treated ? styles.visualTreated : ''}`}
            aria-live="polite"
          >
            <div className={`${styles.orbit} ${styles.orbitA}`} aria-hidden="true" />
            <div className={`${styles.orbit} ${styles.orbitB}`} aria-hidden="true" />
            <div className={`${styles.orbit} ${styles.orbitC}`} aria-hidden="true" />

            <p className={styles.scenarioLabel}>Senaryo</p>
            <p className={styles.scenario}>{scenario.scenario}</p>

            <div className={styles.bands} aria-hidden="true">
              {bandKeys.map((label, index) => (
                <div key={label} className={styles.band}>
                  <div className={styles.bandTrack}>
                    <span
                      className={styles.bandBar}
                      style={{
                        height: barPercent(
                          scenario.bare[index],
                          scenario.treatMul[index],
                          treated,
                        ),
                        background: barColor(index, treated),
                      }}
                    />
                  </div>
                  <span className={styles.bandLabel}>{label}</span>
                </div>
              ))}
            </div>

            <div className={styles.dbStack}>
              {treated ? (
                <span className={styles.delta}>{dbReduction} dB azalma</span>
              ) : null}
              <div className={`${styles.db} ${treated ? styles.dbTreated : ''}`}>
                {dbLevel}
                <span className={styles.dbUnit}>dB</span>
              </div>
              <p className={styles.dbCaption}>{dbCaption}</p>
            </div>
          </div>

          <div className={styles.panel}>
            <div>
              <h3 className={styles.productName}>{scenario.name}</h3>
              <p className={styles.productDesc}>{scenario.desc}</p>
            </div>

            <div className={styles.specs}>
              {scenario.specs.map((spec) => (
                <span key={spec} className={styles.spec}>
                  {spec}
                </span>
              ))}
            </div>

            <div className={styles.modeGroup} role="group" aria-label="Uygulama durumu">
              <button
                type="button"
                className={`${styles.modeBtn} ${!treated ? styles.modeBtnActive : ''}`}
                onClick={() => setTreated(false)}
              >
                Ürünsüz oda
              </button>
              <button
                type="button"
                className={`${styles.modeBtn} ${treated ? styles.modeBtnTreatActive : ''}`}
                onClick={() => setTreated(true)}
              >
                {scenario.treatLabel}
              </button>
            </div>

            <div className={styles.actions}>
              <button
                type="button"
                className={`${styles.playBtn} ${playing ? styles.playBtnOn : ''}`}
                onClick={() => void togglePlay()}
              >
                <span className={styles.playIcon} aria-hidden="true">
                  {playing ? (
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
                      <rect x="2" y="1.5" width="3.5" height="11" rx="0.8" />
                      <rect x="8.5" y="1.5" width="3.5" height="11" rx="0.8" />
                    </svg>
                  ) : (
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
                      <path d="M3 2.2v9.6L12 7 3 2.2Z" />
                    </svg>
                  )}
                </span>
                {playing ? 'Sesi Durdur' : 'Senaryoyu Dinle'}
              </button>
              <a
                className={styles.waBtn}
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Bu ürün için teklif al
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
